/**
 * This component picks a color from the page
 *
 * @author Afifa Saleem <afifa@250mils.com>
 */

import React from 'react';
import './canvas-color-picker.scss';
import PropTypes from "prop-types";
import {colorToHex} from '@Utils/color.util'
import {OverlayTrigger} from "react-bootstrap";
import Tooltip from "react-bootstrap/Tooltip";
import {deviceType, isAndroid, isFirefox, isIOS, isMacOs, isOpera, isSafari, isSamsungBrowser} from 'react-device-detect';
import {setCanvasCursor} from "@Utils/setCanvasCursor";

const DEVICE_TYPE = {
    MOBILE: 'mobile'
};

const CURSOR_OFFSET = 45;

export default class CanvasColorPicker extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            isColorPickMode: false
        };
    }

    /**
     * Render the component
     * @return {JSX.Element}
     */
    render() {
        return <OverlayTrigger
            {...this.props}
            trigger="click"
            placement="top"
            show={this.state.isColorPickMode}
            overlay={
                <Tooltip id="overlay-pick-color" onClick={this.#disableColorPickMode}/>
            }
        >
            <div className='canvas-color-picker-trigger'
                 onClick={this.#enableColorPickMode}/>

        </OverlayTrigger>;
    };

    /**
     * Enter the color picker mode
     */
    #enableColorPickMode = () => {
        this.setState({
            isColorPickMode: true
        });

        this.props.canvasContainer.offsetParent().addClass('-pick-color');
        this.props.beforeColorPicking();
        this.props.canvasContainer.offsetParent().on('click', null, null, this.#disableColorPickMode);

        setCanvasCursor(this.props.canvas, `url("${PMW.util.asset_url('images/postermaker/color-picker.ico')}") 0 25 , auto`);
        this.__onColorPicking = this.#onColorPicking;
        this.__onColorPicked = this.#onColorPicked;
        this.__onRevertColor = this.#onColorRevert;
        this.props.canvas.on('mouse:move', this.__onColorPicking);
        this.props.canvas.on('mouse:down', this.__onColorPicked);
        this.props.canvasContainer.on("mouseout", this.__onRevertColor);
        if (deviceType !== DEVICE_TYPE.MOBILE) {
            Mousetrap.bind('escape', this.#onEscape);
        }
    };

    /**
     * Disables the pick color mode, detach the handlers from canvas
     * which were attached while enabling the picker mode
     */
    #disableColorPickMode = () => {
        if (this.state.isColorPickMode) {
            this.props.canvas.off('mouse:move', this.__onColorPicking);
            this.props.canvas.off('mouse:down', this.__onColorPicked);
            this.props.canvasContainer.off('mouseout', this.__onRevertColor);
            this.props.canvasContainer.offsetParent().removeClass('-pick-color');
            this.props.canvasContainer.offsetParent().off('click', this.#disableColorPickMode);
            setCanvasCursor(this.props.canvas, 'default');
            this.setState({
                isColorPickMode: false
            });
        }
    };

    /**
     * Handler for mouse:move event on canvas
     * @param {event} e event
     */
    #onColorPicking = (e) => {
        const color = this.#getColorFromCoords(e);
        this.props.onColorPicking(color);
    };

    /**
     * Handler for when color is picked from canvas
     */
    #onColorPicked = (e) => {
        const color = this.#getColorFromCoords(e);
        this.props.onColorPicking(color);
        this.props.afterColorPicked();
        setTimeout(() => {
            this.props.onColorPicked();
        }, 200);
        this.#disableColorPickMode();
    };

    /**
     * Handler for when mouse is moved out from canvas to set previous color
     */
    #onColorRevert = () => {
        this.props.onColorPickingRevert();
    };

    /**
     * Handler for escape key pressed in color pick mode
     */
    #onEscape = () => {
        this.#disableColorPickMode();
        this.#onColorRevert();
    };

  /**
   * @returns {boolean}
   */
  #isCustomCursorPositioningUnavailable = () => {
      return (isSafari && (isIOS || isMacOs)) || isSamsungBrowser || isOpera || (isFirefox && isAndroid);
    }

    /**
     * Gets the color on canvas at position where mouse is pointing
     * @param {event} e
     */
    #getColorFromCoords = (e) => {
        const {scale} = this.props;
        const pointer = e.absolutePointer ? e.absolutePointer : e.pointer;
        const p = this.props.canvas.getContext('2d').getImageData((pointer.x * scale), (pointer.y * scale) + (this.#isCustomCursorPositioningUnavailable() ? CURSOR_OFFSET : 0), 1, 1).data;
        return colorToHex({r: p[0], g: p[1], b: p[2]});
    };
}

CanvasColorPicker.propTypes = {
    /**
     * canvas object from which color is to be picked in color picked mode
     * Required when showPickColorFromPage is true
     */
    canvas: PropTypes.object,

    /**
     * canvas object container
     * Required when showPickColorFromPage is true
     */
    canvasContainer: PropTypes.object,

    /**
     * scale of canvas
     * Required when showPickColorFromPage is true
     */
    scale: PropTypes.number,

    /**
     * a handler that invokes when the color is picked
     */
    onColorPicked: PropTypes.func.isRequired,

    /**
     * a handler that invokes when the color is being picked from page
     */
    onColorPicking: PropTypes.func,

    /**
     * a handler that invokes after the color is picked from page
     */
    afterColorPicked: PropTypes.func,

    /**
     * a handler that invokes when color pick mode is turned off without selecting a color
     */
    onColorPickingRevert: PropTypes.func,

    /**
     * a handler that invokes right before color pick mode is turned on
     */
    beforeColorPicking: PropTypes.func,
};
